import {Component, OnDestroy, OnInit, Input, OnChanges, SimpleChanges} from '@angular/core';
import {GlobalApp} from '../../../../tools/global';
import {Router} from '@angular/router';
import {SubscriptionService} from '../../../../api/services/subscription.service';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MatDialog} from '@angular/material/dialog';
import {Subscription} from 'dexie';
import {UserState} from '../../../../api/models/user-state';
import {PreFilterService} from '../../../../api/services/pre-filter.service';
import {PreFilterDialogResult, PreFilterEditorDialogComponent} from '../../../../dialogs/pre-filter-editor-dialog/pre-filter-editor-dialog.component';
import {US_PreFilter} from '../../../../api/models/us_pre-filter';
import {US_SubHeader} from '../../../../api/models/us_sub-header';
import {UserStateEx} from '../../../../api/models-ex/user-state-ex';
import {DialogResultType} from '../../../../dialogs/DialogResult';
import {ConfirmDialogComponent} from '../../../../dialogs/confirm-dialog/confirm-dialog.component';
import {DataOnDemandService} from "../../../../api/services/data-on-demand.service";
import {DodEndPointEx} from "../../../../api/models/dod-end-point-ex";

@Component({
  selector: "pre-filter-management",
  templateUrl: "./pre-filter-management.component.html",
  styleUrls: ["./pre-filter-management.component.css"]
})
export class PreFilterManagementComponent implements OnInit, OnDestroy, OnChanges {

  @Input() subscriptionId: number = -1;

  userState: UserState = {
    reportOnly: false,
    userManagerOnly: false,
    invoiceManagerOnly: false,
    interactives: [],
    modifiedInteractives: [],
    xmlas: [],
    reportExportState: {},
    isInteractiveUser: false,
    isReportExportUser: false,
    isEmbeddedUser: false,
    preFilters: [],
    hasAiChats: false,
    isValidState: false
  };

  userStateSubscription: Subscription = null;
  preFilters: US_PreFilter[] = [];
  allEndPoints: DodEndPointEx[] = [];

  constructor(
    private app: GlobalApp,
    private router: Router,
    private subscriptionService: SubscriptionService,
    private preFilterService: PreFilterService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private dodService: DataOnDemandService
  ) {
    this.app.getAuthorizationStatus().subscribe((result) => {
      if (!result.isValid) {
        this.app.logOut();
        this.router.navigate(["login"]);
      } else {
        this.app.getAuthorizationStatus().subscribe((result) => {
          this.app.initialRefreshUserState(this.subscriptionService);
        });
      }
    });
  }

  ngOnInit() {
    this.userStateSubscription = this.app.userState.subscribe((userState) => {
      this.userState = userState;
      this.reloadPreFilters();
    });
    this.dodService.dataOnDemandGetUserAvailableDodEndPoints().subscribe(dodEndPoints => {
      this.allEndPoints = dodEndPoints;
    });
  }

  ngOnDestroy() {
    if (this.userStateSubscription) {
      this.userStateSubscription.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['subscriptionId']) {
      this.reloadPreFilters();
    }
  }

  reloadPreFilters() {
    this.preFilters = this.userState.preFilters.filter(pf => pf.subscriptionId && pf.subscriptionId == this.subscriptionId);
  }

  public getPreFilterSubscriptions() {
    let filteredSubscriptions: US_SubHeader[] = [];

    if (this.userState) {
      const seenIds = new Set();

      for (let interact of this.userState.interactives) {
        if (interact.canPreFilter) {
          for (let header of interact.subscriptionHeaders) {
            if (!seenIds.has(header.subscriptionId) && header.subscriptionId === this.subscriptionId) {
              filteredSubscriptions.push(header);
              seenIds.add(header.subscriptionId);
            }
          }
        }
      }
    }

    return filteredSubscriptions;
  }

  protected createPreFilter() {
    let subscriptions = this.getPreFilterSubscriptions();

    if(subscriptions.length > 0) {

      let preFilter: US_PreFilter = {
        preFilterId: 0,
        name: 'New Pre Filter',
        userId: -1,
        isDefault: false,
        deleted: false,
        owner: true,
        allowEdit: true,
        shares: []
      };

      PreFilterEditorDialogComponent.showDialog(
        this.dialog,
        preFilter,
        UserStateEx.getPreFilteredShareState(this.userState, preFilter),
        subscriptions,
        this.allEndPoints
      ).subscribe((result) => {
        if (result.ResultType === DialogResultType.Ok) {
          let resultData = result.ResultData as PreFilterDialogResult;
          this.app.pushLoading("Creating Pre Filter...");
          this.preFilterService.preFilterCreatePreFilter(resultData.preFilter).subscribe((newPreFilter) => {
            this.preFilterService.preFilterUpdatePreFilterSharing({preFilterId: newPreFilter.preFilterId, updateSharingStatus: UserStateEx.getSharingStatus(resultData.shareState)}).subscribe((_) => {
              this.app.refreshUserState(this.subscriptionService, () => {
                this.app.popLoading();
                this.snackBar.open("Pre Filter Created.", null, {
                  duration: 5000
                });
              })
            });
          });
        }
      })
    } else {
      this.snackBar.open("No subscriptions found that support pre filters.", null, {
        duration: 5000
      });
    }

    return false;
  }

  protected editPreFilter(preFilter: US_PreFilter) {
    let subscriptions = this.getPreFilterSubscriptions();

    if(subscriptions.length > 0) {

      PreFilterEditorDialogComponent.showDialog(
        this.dialog,
        preFilter,
        UserStateEx.getPreFilteredShareState(this.userState, preFilter),
        subscriptions,
        this.allEndPoints
      ).subscribe((result) => {
        if (result.ResultType === DialogResultType.Ok) {
          let resultData = result.ResultData as PreFilterDialogResult;
          this.app.pushLoading("Updating Pre Filter...");
          this.preFilterService.preFilterUpdatePreFilter(resultData.preFilter).subscribe(_ => {
            this.preFilterService.preFilterUpdatePreFilterSharing({preFilterId: preFilter.preFilterId, updateSharingStatus: UserStateEx.getSharingStatus(resultData.shareState)}).subscribe((_) => {
              this.app.refreshUserState(this.subscriptionService, () => {
                this.app.popLoading();
                this.snackBar.open("Pre Filter Updated.", null, {
                  duration: 5000
                });
              })
            });
          });
        }
      })
    } else {
      this.snackBar.open("No subscriptions found that support pre filters.", null, {
        duration: 5000
      });
    }

    return false;
  }

  protected deletePreFilter(preFilter: US_PreFilter) {
    ConfirmDialogComponent.showDialog(this.dialog, "Confirm Delete", "Are you sure you want to delete this pre filter?")
      .subscribe(result => {
        if (result.ResultType == DialogResultType.Ok) {
          this.app.pushLoading("Deleting Pre Filter...");
          this.preFilterService.preFilterDeletePreFilterByid(preFilter.preFilterId).subscribe(_ => {
            this.app.refreshUserState(this.subscriptionService, () => {
              this.app.popLoading();
              this.snackBar.open("Pre Filter '" + preFilter.name + "' Deleted.", null, {
                duration: 5000
              });
            });
          })
        }
      });

    return false;
  }
}
